import type { TrackingEvent } from '../../types';

/**
 * OpenAI Measurement Pixel event data types. Each standard event requires a
 * matching `type` on the event data object.
 */
export type OpenAiDataType =
    | 'contents'
    | 'customer_action'
    | 'plan_enrollment'
    | 'custom';

export interface OpenAiEventData {
    eventName: string;
    /** True when the event is sent as OpenAI's `custom` type with a custom_event_name. */
    isCustom: boolean;
    dataType: OpenAiDataType;
}

/**
 * Maps an internal tracking event onto the ChatGPT Ads Measurement Pixel name
 * and data shape.
 *
 * Returns null when the event has no OpenAI equivalent, so the tracker can
 * skip it the same way the Meta tracker skips unmapped events.
 */
export function getOpenAiEvent(event: TrackingEvent): OpenAiEventData | null {
    switch (event) {
        case 'PageView':
            return {
                eventName: 'page_viewed',
                isCustom: false,
                dataType: 'contents',
            };
        case 'ViewProduct':
            return {
                eventName: 'contents_viewed',
                isCustom: false,
                dataType: 'contents',
            };
        case 'PerformSearch':
            return {
                eventName: 'search_performed',
                isCustom: true,
                dataType: 'custom',
            };

        // A sent meeting request or booking is a lead of the same weight as an
        // enquiry, so both are OpenAI's standard lead event.
        case 'CreatedEnquiry':
        case 'BookMeetingSubmit':
            return {
                eventName: 'lead_created',
                isCustom: false,
                dataType: 'customer_action',
            };
        case 'PhoneReveal':
            return {
                eventName: 'phone_revealed',
                isCustom: true,
                dataType: 'custom',
            };
        case 'Website':
            return {
                eventName: 'website_referral',
                isCustom: true,
                dataType: 'custom',
            };
        case 'DownloadsClick':
        case 'DownloadsLoggedOutClick':
            return {
                eventName: 'file_downloaded',
                isCustom: true,
                dataType: 'custom',
            };
        case 'BookConsultationReveal':
            // Reveal of the consultation CTA only — not a completed booking.
            return {
                eventName: 'book_consultation_revealed',
                isCustom: true,
                dataType: 'custom',
            };
        case 'EnquiryReveal':
            return {
                eventName: 'enquiry_reveal',
                isCustom: true,
                dataType: 'custom',
            };
        case 'BookMeetingReveal':
            // The booking gate opening — not a request or a booking.
            return {
                eventName: 'book_meeting_reveal',
                isCustom: true,
                dataType: 'custom',
            };
        case 'EnquiryEngagement':
            return {
                eventName: 'enquiry_engagement',
                isCustom: true,
                dataType: 'custom',
            };
        case 'SaveSubmit':
            return {
                eventName: 'saved',
                isCustom: true,
                dataType: 'custom',
            };
        case 'HDESubmission':
            return {
                eventName: 'registration_completed',
                isCustom: false,
                dataType: 'customer_action',
            };

        case 'AddToCart':
            return {
                eventName: 'items_added',
                isCustom: false,
                dataType: 'contents',
            };
        case 'InitiateCheckout':
            return {
                eventName: 'checkout_started',
                isCustom: false,
                dataType: 'contents',
            };
        case 'Purchase':
            return {
                eventName: 'order_created',
                isCustom: false,
                dataType: 'contents',
            };

        default:
            return null;
    }
}
